import React, { FC } from 'react';

import { Group } from '@mantine/core';

import { CompanyProfile, Compliance } from 'models';

import { BadgePill } from 'components/badges/BadgePill';
import { CurrentOS } from 'components/icons/certification/CurrentOS';
import { EmergeAlliance } from 'components/icons/certification/EmergeAlliance';
import { ODCA } from 'components/icons/certification/ODCA';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

const DEFAULT_SIZE = 22;

type OrganizationKey = Compliance.CURRENT_OS | Compliance.ODCA | Compliance.EMERGE;

const organizations: Record<OrganizationKey, (size: number) => React.ReactNode> = {
    [Compliance.CURRENT_OS]: (size) => <CurrentOS size={Math.round((size * 16) / DEFAULT_SIZE)} />,
    [Compliance.ODCA]: (size) => <ODCA size={size} />,
    [Compliance.EMERGE]: (size) => <EmergeAlliance size={size} />,
};

const OrganizationBadges: FC<{
    organizations: OrganizationKey[];
    size?: number;
}> = ({ organizations: shownOrganizations, size = DEFAULT_SIZE }) => {
    if (shownOrganizations.length === 0) {
        return null;
    }

    return (
        <Group align="stretch" gap="xs">
            {shownOrganizations.map((key) => (
                <BadgePill key={key} style={{ filter: 'grayscale(1)' }}>
                    {organizations[key](size)}
                </BadgePill>
            ))}
        </Group>
    );
};

const useMemberOrganizations = (compliance: CompanyProfile['compliance']): OrganizationKey[] => {
    const { memberOrganizations } = useWorkspaceConfig();

    return (Object.keys(organizations) as OrganizationKey[]).filter(
        (key) => compliance?.[key] && memberOrganizations?.includes(key),
    );
};

export { OrganizationBadges, useMemberOrganizations };
